import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, DoorOpen } from 'lucide-react';
import type { Unit, UnitAllowedDevice, AccessDevice } from '@yawi/types';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { Button } from '../atoms/Button';
import { SearchableSelect } from '@/core/ui/components';

interface Props {
  unit: Unit;
  onClose: () => void;
}

export const UnitAllowedDevicesModal: React.FC<Props> = ({ unit, onClose }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { unitRepo, deviceRepo } = useServices();
  const ability = useAbility();
  const canManageAllUnitDevices = ability.can('manage', 'all');
  const canAddUnitDevices = canManageAllUnitDevices || ability.can('update', 'UnitAllowedDevice');
  const canRemoveUnitDevices = canManageAllUnitDevices || ability.can('delete', 'UnitAllowedDevice');

  const [allowed, setAllowed] = useState<UnitAllowedDevice[]>([]);
  const [allDevices, setAllDevices] = useState<AccessDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allowedList, devicesList] = await Promise.all([
        unitRepo.getAllowedDevices(unit.id),
        canAddUnitDevices ? deviceRepo.getAllDevices() : Promise.resolve([]),
      ]);
      setAllowed(allowedList);
      setAllDevices(devicesList);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(msg, t('admin.units.allowed_devices.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id]);

  const allowedDeviceIds = useMemo(() => new Set(allowed.map((d) => d.id)), [allowed]);

  const availableOptions = useMemo(
    () =>
      allDevices
        .filter((d) => !allowedDeviceIds.has(d.id))
        .map((d) => ({
          value: d.id,
          label: `${d.name}${d.location ? ` — ${d.location}` : ''}`,
        })),
    [allDevices, allowedDeviceIds]
  );

  const handleAdd = async () => {
    if (!selectedDeviceId) return;
    setAdding(true);
    try {
      const result = await unitRepo.addAllowedDevice(unit.id, selectedDeviceId);
      if (result.statusCode === 200) {
        toast.info(t('admin.units.allowed_devices.already_assigned'));
      } else {
        toast.success(t('admin.units.allowed_devices.added'));
      }
      setSelectedDeviceId('');
      await loadData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(msg, t('admin.units.allowed_devices.add_error'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (deviceId: string) => {
    setRemovingId(deviceId);
    try {
      await unitRepo.removeAllowedDevice(unit.id, deviceId);
      toast.success(t('admin.units.allowed_devices.removed'));
      await loadData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(msg, t('admin.units.allowed_devices.remove_error'));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
              <DoorOpen size={20} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-lg">
                {t('admin.units.allowed_devices.title')}
              </h3>
              <p className="text-xs text-slate-500">{unit.name}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label={t('common.close')}>
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          <p className="text-sm text-slate-600">
            {t('admin.units.allowed_devices.description')}
          </p>

          {canAddUnitDevices && (
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('admin.units.allowed_devices.add_device')}
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    value={selectedDeviceId || null}
                    onChange={value => setSelectedDeviceId(value ?? '')}
                    options={availableOptions}
                    placeholder={
                      availableOptions.length === 0
                        ? t('admin.units.allowed_devices.no_available')
                        : t('admin.units.allowed_devices.select_device')
                    }
                    isDisabled={loading || adding || availableOptions.length === 0}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleAdd}
                  disabled={!selectedDeviceId || adding}
                  icon={<Plus size={16} />}
                >
                  {adding ? t('common.loading') : t('common.add')}
                </Button>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">
              {t('admin.units.allowed_devices.assigned')} ({allowed.length})
            </h4>

            {loading ? (
              <div className="p-8 text-center text-sm text-slate-400">
                {t('common.loading')}...
              </div>
            ) : allowed.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-200 rounded-lg">
                <DoorOpen size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {t('admin.units.allowed_devices.empty')}
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {allowed.map((device) => (
                  <li
                    key={device.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          device.status === 'ONLINE' ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                        title={device.status}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">
                          {device.name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                           {device.location === "Controladora" ? device.name : (device.location || device.externalDeviceId)}
                        </p>
                      </div>
                    </div>
                    {canRemoveUnitDevices && (
                      <button
                        onClick={() => handleRemove(device.id)}
                        disabled={removingId === device.id}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        aria-label={t('admin.units.allowed_devices.remove')}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-100 bg-slate-50/50">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
};
